import React, { useEffect, useState } from "react";
import { Package, Users, Star, Settings as SettingsIcon, Save, Inbox, TrendingUp, Route, Wrench, HelpCircle, Flag, Warehouse, Gift, ShieldCheck, PhoneCall, PackageSearch, Activity as ActivityIcon, Image as ImageIconTab } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { SubmissionCard, TrackingCard } from "./StaffDashboard";
import { InquiryInbox } from "../components/InquiryInbox";
import { WhatsAppSettingsFields } from "../components/WhatsAppSettingsFields";
import { MessageCircle } from "lucide-react";
import { CorridorManager, ServiceManager, FaqManager, CountryManager, WarehouseManager, EmployeeManager, OfferManager, InsuranceManager, ContactManager, ImageManager } from "./AdminContent";

export default function AdminPanel() {
  const { settings, saveSettings, reviews, refreshReviews, warehouses } = useApp();
  const [subs, setSubs] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [empCount, setEmpCount] = useState(0);
  const [form, setForm] = useState(settings);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [settingsDirty, setSettingsDirty] = useState(false);

  const loadSubs = () => { api.getSubmissions().then(setSubs).catch(() => {}); api.getActivity().then(setActivity).catch(() => {}); };
  const loadTracks = () => api.getTracking().then(setTracks).catch(() => {});
  useEffect(() => { loadSubs(); loadTracks(); api.getActivity().then(setActivity).catch(() => {}); api.getEmployees().then((e) => setEmpCount(e.length)).catch(() => {}); refreshReviews(); }, []);
  useEffect(() => { if (!settingsDirty) setForm(settings); }, [settings, settingsDirty]);

  const refreshActivity = () => api.getActivity().then(setActivity).catch(() => {});

  const setS = (k) => (e) => { setSettingsDirty(true); setForm({ ...form, [k]: e.target.value }); };
  const saveCompanySettings = async () => {
    setSettingsError(""); setSettingsSaving(true);
    try { await saveSettings(form); setSettingsDirty(false); toast.success("Settings saved across the site!"); }
    catch (error) {
      const detail = error?.response?.data?.detail;
      setSettingsError(Array.isArray(detail) ? detail.map((d) => d.msg).join(". ") : typeof detail === "string" ? detail : "Could not save settings. Please try again.");
    } finally { setSettingsSaving(false); }
  };

  const stats = [
    { icon: Inbox, label: "Total requests", value: subs.length },
    { icon: TrendingUp, label: "New requests", value: subs.filter((s) => s.status === "New").length },
    { icon: PackageSearch, label: "Tracking requests", value: tracks.length },
    { icon: Users, label: "Employees", value: empCount },
  ];

  const settingFields = [
    ["Email", "email", "hello@smartlogistics.eu"],
    ["Address", "address", "Via Logistica 24, Milan"],
    ["Facebook URL", "facebook", "https://facebook.com/…"],
    ["Instagram URL", "instagram", "https://instagram.com/…"],
    ["TikTok URL", "tiktok", "https://tiktok.com/@…"],
    ["YouTube URL", "youtube", "https://youtube.com/@…"],
  ];

  const tab = (v, Icon, label) => (
    <TabsTrigger data-testid={`admin-${v}-tab-trigger`} value={v} className="data-[state=active]:bg-orange-500 data-[state=active]:text-white gap-1.5"><Icon className="w-4 h-4" /> {label}</TabsTrigger>
  );

  return (
    <div className="max-w-6xl mx-auto px-5 py-12">
      <div className="mb-8">
        <p className="text-orange-500 text-sm font-semibold uppercase tracking-wider">Admin Panel</p>
        <h1 className="font-display font-bold text-3xl mt-2">Control center</h1>
        <p className="text-neutral-600 mt-1">Everything on the website is editable from here.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <div key={s.label} className="glass rounded-2xl p-5 flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl orange-gradient grid place-items-center shrink-0"><s.icon className="w-5 h-5 text-white" /></div>
            <div><p className="font-display font-bold text-2xl">{s.value}</p><p className="text-xs text-neutral-600">{s.label}</p></div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="requests">
        <TabsList className="bg-black/[0.03] border border-black/10 mb-6 flex-wrap h-auto">
          {tab("requests", Package, "Requests")}
          {tab("inquiries", MessageCircle, "Inquiries")}
          {tab("tracking", PackageSearch, "Tracking")}
          {tab("activity", ActivityIcon, "Activity")}
          {tab("corridors", Route, "Corridors & Rates")}
          {tab("services", Wrench, "Services")}
          {tab("faqs", HelpCircle, "FAQs")}
          {tab("countries", Flag, "Countries")}
          {tab("warehouses", Warehouse, "Warehouses")}
          {tab("offers", Gift, "Offers")}
          {tab("insurance", ShieldCheck, "Insurance")}
          {tab("images", ImageIconTab, "Site Images")}
          {tab("employees", Users, "Employees")}
          {tab("reviews", Star, "Reviews")}
          {tab("settings", SettingsIcon, "Settings")}
        </TabsList>

        <TabsContent value="inquiries" data-testid="admin-inquiries-tab"><InquiryInbox /></TabsContent>
        <TabsContent value="requests">
          {subs.length ? <div className="space-y-4">{subs.map((s) => <SubmissionCard key={s.id} s={s} onChange={loadSubs} />)}</div>
            : <div className="glass rounded-2xl p-12 text-center"><Package className="w-12 h-12 text-neutral-300 mx-auto mb-4" /><p className="text-neutral-600">No customer requests yet.</p></div>}
        </TabsContent>

        <TabsContent value="tracking" data-testid="admin-tracking-tab">
          {tracks.length ? (
            <div className="space-y-3">
              {tracks.map((t) => <TrackingCard key={t.id} t={t} onActivity={refreshActivity} onDeleted={() => { loadTracks(); refreshActivity(); }} />)}
            </div>
          ) : <div className="glass rounded-2xl p-12 text-center"><PackageSearch className="w-12 h-12 text-neutral-300 mx-auto mb-4" /><p className="text-neutral-600">No tracking requests yet.</p></div>}
        </TabsContent>

        <TabsContent value="activity" data-testid="admin-activity-tab">
          {activity.length ? (
            <div className="glass rounded-2xl divide-y divide-black/[0.06] overflow-hidden">
              {activity.map((a) => (
                <div key={a.id} className="flex items-start gap-3 p-4" data-testid={`activity-row-${a.id}`}>
                  <div className="w-9 h-9 rounded-full orange-gradient grid place-items-center text-white text-xs font-bold shrink-0 mt-0.5">{(a.actorName || "?").charAt(0)}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-neutral-800">
                      <span className="font-semibold">{a.actorName || a.actorEmail}</span>
                      <span className="text-neutral-400"> ({a.actorRole})</span> {a.action} — <span className="font-medium">{a.label}</span>
                    </p>
                    {a.detail && <p className="text-xs text-neutral-500 mt-0.5">{a.detail}</p>}
                  </div>
                  <p className="text-xs text-neutral-400 whitespace-nowrap shrink-0">{new Date(a.createdAt).toLocaleString()}</p>
                </div>
              ))}
            </div>
          ) : <div className="glass rounded-2xl p-12 text-center"><ActivityIcon className="w-12 h-12 text-neutral-300 mx-auto mb-4" /><p className="text-neutral-600">No staff activity recorded yet.</p></div>}
        </TabsContent>

        <TabsContent value="corridors"><CorridorManager /></TabsContent>        <TabsContent value="services"><ServiceManager /></TabsContent>
        <TabsContent value="faqs"><FaqManager /></TabsContent>
        <TabsContent value="countries"><CountryManager /></TabsContent>
        <TabsContent value="warehouses"><WarehouseManager /></TabsContent>
        <TabsContent value="offers"><OfferManager /></TabsContent>
        <TabsContent value="insurance"><InsuranceManager /></TabsContent>
        <TabsContent value="images"><ImageManager /></TabsContent>
        <TabsContent value="employees"><EmployeeManager /></TabsContent>

        <TabsContent value="reviews">
          {reviews.length ? (
            <div className="grid md:grid-cols-2 gap-4">{reviews.map((r) => (
              <div key={r.id} className="glass rounded-2xl p-5">
                <div className="flex gap-0.5 mb-2">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-orange-500 text-orange-500" />)}</div>
                <p className="text-sm text-neutral-700 leading-relaxed">"{r.text}"</p>
                <p className="text-sm font-semibold mt-3">{r.name} <span className="text-neutral-500 font-normal">· {r.city || "Customer"}</span></p>
                <p className="text-xs text-neutral-300 mt-1">{new Date(r.createdAt).toLocaleString()}</p>
              </div>
            ))}</div>
          ) : <div className="glass rounded-2xl p-12 text-center"><Star className="w-12 h-12 text-neutral-300 mx-auto mb-4" /><p className="text-neutral-600">No reviews submitted yet.</p></div>}
        </TabsContent>

        <TabsContent value="settings">
          <div className="glass rounded-2xl p-6 max-w-3xl">
            <h3 className="font-display font-semibold text-lg mb-1">Company &amp; contact settings</h3>
            <div className="mt-6"><WhatsAppSettingsFields form={form} onChange={setS} /></div>
            <div className="grid md:grid-cols-2 gap-4">
              {settingFields.map(([label, key, ph]) => (
                <div key={key}>
                  <Label className="text-sm text-neutral-700">{label}</Label>
                  <Input data-testid={`admin-settings-${key}`} value={form[key] || ""} onChange={setS(key)} placeholder={ph} className="bg-black/[0.03] border-black/10 mt-1.5" />
                </div>
              ))}
            </div>
            {settingsError && <p data-testid="admin-settings-error" role="alert" className="mt-5 text-sm text-red-700">{settingsError}</p>}
            <Button data-testid="admin-settings-save" disabled={settingsSaving} className="orange-gradient text-white border-0 gap-2 hover:opacity-90 mt-6" onClick={saveCompanySettings}><Save className="w-4 h-4" /> {settingsSaving ? "Saving…" : "Save changes"}</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
